/**
 * Phase 2b — Decryption-reveal hook (v3, cyber-cipher).
 *
 * Reveals `target` as if a quantum decapsulation routine is resolving it,
 * cycling each character through a rich cipher alphabet (mixed letters,
 * digits, hex-y symbols, lambdas) before a brief "lock-in flash" snaps
 * the final glyph in place.
 *
 * Returns `{ text, lockedFlags }`:
 *   - `text`        — the current scrambled/partially-resolved string.
 *   - `lockedFlags` — a per-character boolean array, true once that
 *                     character has just locked in. Consumers can use it
 *                     to trigger a CSS "flash" class on the matching span.
 *
 * The reveal lasts ~2400ms by default, but each character has a
 * randomized lock-in time so the reveal doesn't read as a left-to-right
 * wave — it feels like blocks decoding asynchronously.
 *
 * Honours `prefers-reduced-motion` — that branch shows the final text
 * immediately, no animation.
 */
import { useEffect, useState } from "react";

// Rich cipher alphabet — visually evokes hex digests, base64 ciphertext,
// and the kind of mixed-glyph stream you'd see in a key-derivation log.
// Avoid lowercase "l" and uppercase "I" because they collapse to a thin
// vertical bar at our heading sizes and read as a layout glitch.
const CIPHER_GLYPHS = (
  "ABCDEFGHJKMNPQRSTUVWXYZ" +
  "0123456789" +
  "▒░▓█·" +
  "@#$%&*<>?/\\"
).split("");

const randCipher = () =>
  CIPHER_GLYPHS[Math.floor(Math.random() * CIPHER_GLYPHS.length)];


export default function useDecryptionReveal(target, durationMs = 2400) {
  const [text, setText] = useState("");
  const [lockedFlags, setLockedFlags] = useState([]);

  useEffect(() => {
    if (!target) {
      setText("");
      setLockedFlags([]);
      return undefined;
    }
    if (typeof window !== "undefined" &&
        window.matchMedia &&
        window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      setText(target);
      setLockedFlags(target.split("").map(() => true));
      return undefined;
    }

    // Per-character lock-in time: a base wave (left → right) plus a
    // tiny jitter so adjacent characters don't all flip in lockstep.
    const baseWave = (i, len) => (i / Math.max(1, len - 1)) * 0.85;
    const lockTimes = Array.from({ length: target.length }, (_, i) => {
      const jitter = (Math.random() - 0.5) * 0.22;          // ±11 % of duration
      return Math.max(0, Math.min(1, baseWave(i, target.length) + jitter));
    });

    // Cipher-cycling cadence: each char holds a glyph for ~80ms before
    // picking a new one. The result is a readable scramble (not a blur)
    // that reads as "decryption in progress".
    const CYCLE_MS = 80;

    // Track which characters have already been "announced" as locked
    // so we only flash once per character.
    const announced = new Array(target.length).fill(false);

    const start = performance.now();
    let raf = 0;
    const tick = (now) => {
      const elapsed = now - start;
      const progress = Math.min(elapsed / durationMs, 1);
      const cycleStep = Math.floor(elapsed / CYCLE_MS);

      let out = "";
      const newLocked = new Array(target.length).fill(false);
      for (let i = 0; i < target.length; i += 1) {
        const targetCh = target[i];
        if (targetCh === " ") {
          out += " ";
          continue;
        }
        const lockAt = lockTimes[i];
        if (progress >= lockAt) {
          out += targetCh;
          if (!announced[i]) {
            announced[i] = true;
            newLocked[i] = true;
          }
        } else {
          // Cipher scramble. We deterministically derive the glyph from
          // (i, cycleStep) so the same character doesn't flicker every
          // frame — it holds for one CYCLE_MS step, then changes.
          const seed = (i * 31 + cycleStep * 17 + Math.floor(lockAt * 100)) | 0;
          out += CIPHER_GLYPHS[((seed % CIPHER_GLYPHS.length) + CIPHER_GLYPHS.length) % CIPHER_GLYPHS.length];
        }
      }
      setText(out);
      if (newLocked.some(Boolean)) {
        setLockedFlags((prev) => {
          // Merge: keep any previously-locked, set newly-locked true.
          const next = prev.length === target.length ? [...prev] : new Array(target.length).fill(false);
          for (let i = 0; i < target.length; i += 1) {
            if (newLocked[i]) next[i] = true;
          }
          return next;
        });
        // Auto-clear the "just locked" pulse after the CSS animation
        // duration so the same character can't flash again on re-mount.
        setTimeout(() => {
          setLockedFlags((prev) => prev.map((v, i) => (newLocked[i] ? false : v)));
        }, 360);
      }
      if (progress < 1) {
        raf = requestAnimationFrame(tick);
      } else {
        setText(target);
      }
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, durationMs]);

  // Backwards compatibility: callers that destructure as a string still work
  // because we attach the array as a property on the String instance via a
  // Proxy is overkill — instead we expose two named values and let the
  // (one) consumer read whichever it needs. The existing single-string
  // consumer path is preserved by returning `text` directly when no
  // destructure is performed.
  // To keep the API tiny, we just return an object and migrate the caller.
  return { text, lockedFlags };
}


// Random ciphertext stream of the given length — used as a decorative
// "binary/cipher river" alongside the H1 (QuantumEmotion-style).
export function makeCipherStream(length = 240) {
  let out = "";
  for (let i = 0; i < length; i += 1) out += randCipher();
  return out;
}
